/**
 * World 5 — "Mirror Isle" (Mystery, levels 41–50). Act I, chapter 5: the
 * Zaricat Guardian that wears your face. Levels/caps stamped from World 5's band
 * (41→50). Because every rival is a REFLECTION of some traveler, squads are
 * mixed-element by design and the world's `element` is 'Mystery' (the difficulty
 * test relaxes its per-element check for non-elemental worlds). Finale boss
 * (slot 0) is Zaricat — Glimmer, the Mirror Guardian. Dialogue voiced at Pokémon
 * Red trainer complexity (cast: Timmy, Mimi, Lulu, Aggie). Clan Z's Zip and
 * Zap trail the player here too, and the isle throws wild copies from the glass.
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  ZARICAT,
  TORO,
  AQUA,
  FERNAIL,
  SUIMON,
  PINKY,
  YETI,
  BIREON,
  KOBU,
  PIKOTO,
  PEFAREON,
  SUMIYA,
  RAY,
  GORLY,
  TALION,
  DRAGO,
  TRIXON,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD5_NODE_STORIES as S,
  WORLD5_ZONE_STORIES as Z,
  WORLD5_TAGLINE,
} from './stories/world5.js';

export const WORLD_5: CampaignWorld = {
  worldId: 'w5',
  name: 'Mirror Isle',
  element: 'Mystery',
  featuredSpeciesId: ZARICAT,
  story: WORLD5_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z12',
      name: 'Glass Shallows',
      element: 'Mystery',
      story: Z['z12'],
      nodes: [
        node({
          nodeId: 'z12-n1',
          kind: 'normal',
          rivalName: 'Mimi',
          rivalTitle: 'Mirror Lass',
          ...S['z12-n1'],
          squad: [rival(FERNAIL, 'Mimi'), rival(AQUA, 'Wade'), rival(SUIMON, 'Net')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: FERNAIL,
        }),
        giftNode({
          nodeId: 'z12-g1',
          giver: 'Glass Seer',
          label: 'Trailside Gift',
          story: 'A seer peering into still glass waves you closer.',
          line: "The mirror showed me this, just for you. Take it!",
          reward: { tracks: giftTracks(ZARICAT) },
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z12-n2',
          kind: 'normal',
          rivalName: 'Lulu',
          rivalTitle: 'Mirror Lass',
          ...S['z12-n2'],
          squad: [rival(PIKOTO, 'Lulu'), rival(BIREON, 'Tag'), rival(KOBU, 'Skip')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PIKOTO,
        }),
        node({
          nodeId: 'z12-n3',
          kind: 'normal',
          rivalName: 'Timmy',
          rivalTitle: 'Mirror Youngster',
          ...S['z12-n3'],
          squad: [rival(SUIMON, 'Timmy'), rival(YETI, 'Deep'), rival(PINKY, 'Pearl')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: SUIMON,
        }),
        node({
          nodeId: 'z12-n4',
          kind: 'zone_boss',
          rivalName: 'Pale Twin',
          rivalTitle: 'First Mirror',
          ...S['z12-n4'],
          squad: [rival(ZARICAT, 'Pale'), rival(TRIXON, 'Twin'), rival(AQUA, 'Glass')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: ZARICAT,
        }),
      ],
    },
    {
      zoneId: 'z13',
      name: 'Hall of Faces',
      element: 'Mystery',
      story: Z['z13'],
      nodes: [
        node({
          nodeId: 'z13-n1',
          kind: 'normal',
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z13-n1'],
          squad: [rival(TALION, 'Dash'), rival(KOBU, 'Coin'), rival(SUMIYA, 'Shard Book')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        giftNode({
          nodeId: 'z13-g1',
          giver: 'Quiet Monk',
          label: 'Training Charm',
          story: 'A monk folding a paper charm bows to you.',
          line: 'This charm calms a restless heart. Here.',
          reward: { xp: giftXp(4) },
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z13-n2',
          kind: 'normal',
          rivalName: 'Mirror Timmy',
          rivalTitle: 'Copy Kid',
          ...S['z13-n2'],
          squad: [rival(BIREON, 'Chorpy'), rival(PIKOTO, 'Cluckers'), rival(KOBU, 'Munch')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: BIREON,
        }),
        node({
          nodeId: 'z13-n3',
          kind: 'normal',
          rivalName: 'Aggie',
          rivalTitle: 'Mirror Medium',
          ...S['z13-n3'],
          squad: [rival(SUMIYA, 'Aggie'), rival(RAY, 'Quill'), rival(GORLY, 'Tome')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: SUMIYA,
        }),
        node({
          nodeId: 'z13-nw1',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Glasswing',
          rivalTitle: 'Wild Suikamon',
          ...S['z13-nw1'],
          squad: [rival(DRAGO, 'Glint'), rival(AQUA, 'Ripple'), rival(SUIMON, 'Shard')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: DRAGO,
        }),
        node({
          nodeId: 'z13-n4',
          kind: 'zone_boss',
          rivalName: 'Blank Face',
          rivalTitle: 'Hall Keeper',
          ...S['z13-n4'],
          squad: [rival(ZARICAT, 'Hollow'), rival(TORO, 'Visage'), rival(SUMIYA, 'Mask')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: ZARICAT,
        }),
      ],
    },
    {
      zoneId: 'z14',
      name: 'Still Water',
      element: 'Mystery',
      story: Z['z14'],
      nodes: [
        node({
          nodeId: 'z14-n1',
          kind: 'normal',
          rivalName: 'Mirror Keeper',
          rivalTitle: 'Lost Keeper',
          ...S['z14-n1'],
          squad: [rival(FERNAIL, 'Stay'), rival(PINKY, 'Dusk'), rival(AQUA, 'Vow')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: FERNAIL,
        }),
        giftNode({
          nodeId: 'z14-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'You pass a mirror shrine. It shows no face, not even yours.',
          line: 'The mirror leaves a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(ZARICAT, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z14-n2',
          kind: 'normal',
          rivalName: 'Your Shadow',
          rivalTitle: 'Tired You',
          ...S['z14-n2'],
          squad: [rival(TRIXON, 'Murk'), rival(TORO, 'Usurp'), rival(DRAGO, 'Eclipse')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z14-n5',
          kind: 'normal',
          rivalName: 'Zap',
          rivalTitle: 'Clan Z',
          ...S['z14-n5'],
          squad: [rival(PEFAREON, 'Shade'), rival(PIKOTO, 'Nick'), rival(TRIXON, 'Tally')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PEFAREON,
        }),
        node({
          nodeId: 'z14-nw2',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Stillmaw',
          rivalTitle: 'Wild Suikamon',
          ...S['z14-nw2'],
          squad: [rival(GORLY, 'Hush'), rival(RAY, 'Glare'), rival(TRIXON, 'Static')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: GORLY,
        }),
        node({
          nodeId: 'z14-n3',
          kind: 'mini_boss',
          rivalName: 'Other You',
          rivalTitle: 'Perfect You',
          ...S['z14-n3'],
          squad: [rival(SUMIYA, 'Truer'), rival(SUIMON, 'Polished'), rival(PIKOTO, 'Apex')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: SUMIYA,
        }),
        node({
          nodeId: 'z14-n4',
          kind: 'zone_boss',
          rivalName: 'Glimmer',
          rivalTitle: 'Mirror Guardian',
          ...S['z14-n4'],
          squad: [rival(ZARICAT, 'Glimmer'), rival(TRIXON, 'Silver'), rival(AQUA, 'Reflection')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: ZARICAT,
          soulSpeciesId: ZARICAT,
        }),
      ],
    },
  ],
};
